import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { gql } from "../__generated__";

export const GET_PUBLISHED_SURVEY_SCHEMA = gql(`
  query GetPublishedSurveySchema($condition: String!) {
    publishedSurveySchema(condition: $condition) {
      id
      condition
      version
      schemaData
      description
      isPublished
    }
  }
`);

interface UseSurveySchemaByConditionResult {
  surveySchema: object | null;
  surveySchemaId: string | null;
  loading: boolean;
  error: Error | null;
}

interface SurveySchemaState {
  surveySchema: object | null;
  surveySchemaId: string | null;
  loading: boolean;
  error: Error | null;
}

export const useSurveySchemaByCondition = (
  condition: string,
): UseSurveySchemaByConditionResult => {
  const [state, setState] = useState<SurveySchemaState>({
    surveySchema: null,
    surveySchemaId: null,
    loading: true,
    error: null,
  });

  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery(GET_PUBLISHED_SURVEY_SCHEMA, {
    variables: { condition },
    skip: !condition,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!condition) {
      setState({
        surveySchema: null,
        surveySchemaId: null,
        loading: false,
        error: null,
      });
      return;
    }

    if (queryLoading) {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));
      return;
    }

    if (queryError) {
      setState({
        surveySchema: null,
        surveySchemaId: null,
        loading: false,
        error:
          queryError instanceof Error
            ? queryError
            : new Error("Failed to fetch survey schema"),
      });
      return;
    }

    const surveySchema = data?.publishedSurveySchema;

    if (!surveySchema) {
      setState({
        surveySchema: null,
        surveySchemaId: null,
        loading: false,
        error: new Error(
          `No published survey schema found for condition: ${condition}`,
        ),
      });
      return;
    }

    try {
      const parsedSchema = JSON.parse(surveySchema.schemaData);

      setState({
        surveySchema: parsedSchema,
        surveySchemaId: surveySchema.id,
        loading: false,
        error: null,
      });
    } catch (parseError) {
      console.error("Error parsing survey schema data:", parseError);
      setState({
        surveySchema: null,
        surveySchemaId: null,
        loading: false,
        error: new Error("Failed to parse survey schema data"),
      });
    }
  }, [condition, data, queryLoading, queryError]);

  return state;
};
